/**
 * Moving an organization owner between their organization console and one of
 * its institutes (ORGANIZATION-GROUPS-PLAN.md §5.4).
 *
 * Both are sign-in contexts the server synthesises and re-checks on every
 * request (InstituteMembership/context_service.py):
 *
 *   org:<oid>            the organization console
 *   org:<oid>:i:<iid>    "View institute" - owner of that one institute
 *
 * Switching re-issues this session's tokens through select-context, exactly
 * as the sign-in picker does, then reloads the page so permissions, the
 * sidebar and every cached institute id are rebuilt from the new token.
 */
import axios from "axios";

import baseURL from "@/http";
import { getValidToken } from "@/pages/public/Login/checkTokenValidity";
import { buildInstitutePath } from "@/utils/instituteRoute";
import { getTenantClaims } from "./tenantClaims";

const ORG_PREFIX = "org:";

export interface OrganizationSession {
  organizationId: number;
  /** Set while viewing one institute; null in the console. */
  instituteId: number | null;
}

/** The organization this session belongs to, from the signed token, or null. */
export const getOrganizationSession = (): OrganizationSession | null => {
  const uid = getTenantClaims()?.membershipUid ?? "";
  if (!uid.startsWith(ORG_PREFIX)) return null;
  const parts = uid.slice(ORG_PREFIX.length).split(":");
  // Digits only: Number("") is 0, which would read "org:" as organization 0.
  if (!/^\d+$/.test(parts[0])) return null;
  const organizationId = Number(parts[0]);
  if (parts.length === 3 && parts[1] === "i" && /^\d+$/.test(parts[2])) {
    return { organizationId, instituteId: Number(parts[2]) };
  }
  return parts.length === 1 ? { organizationId, instituteId: null } : null;
};

async function switchContext(membershipUid: string): Promise<void> {
  const token = await getValidToken();
  const { data } = await axios.post(
    `${baseURL}/auth/select-context/`,
    { membership_uid: membershipUid },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!data?.access || !data?.refresh) {
    throw new Error("The session could not be switched.");
  }
  localStorage.setItem("access_token", data.access);
  localStorage.setItem("refresh_token", data.refresh);
}

/** A tab of the institute shell to land on (InstituteDetail's main / sub tab ids). */
export interface InstituteTab {
  tab: string;
  sub?: string;
}

/**
 * Open one of the organization's institutes as its owner, optionally on a
 * given tab. InstituteDetail restores its tab from these two keys on load, so
 * writing them first is how a dashboard list row lands on, say, People →
 * Students rather than wherever that institute was last left.
 */
export async function enterInstitute(
  organizationId: number,
  instituteId: number,
  instituteName?: string | null,
  open?: InstituteTab,
): Promise<void> {
  await switchContext(`${ORG_PREFIX}${organizationId}:i:${instituteId}`);
  if (open?.tab) {
    try {
      localStorage.setItem(`institute_${instituteId}_activeTab`, open.tab);
      localStorage.setItem(`institute_${instituteId}_activeSubTab`, open.sub ?? "");
    } catch {
      // Storage blocked: the institute still opens, on its default tab.
    }
  }
  window.location.assign(buildInstitutePath(instituteId, instituteName));
}

/** The institute an organization sign-in landed in: its "home". */
const HOME_INSTITUTE_KEY = "organization_home_institute";

/** True when this organization session is in an institute OTHER than its home
 *  (reached with "View institute") - the only place a Back button means anything. */
export const awayFromHomeInstitute = (): boolean => {
  const session = getOrganizationSession();
  if (!session || session.instituteId === null) return false;
  try {
    const home = localStorage.getItem(HOME_INSTITUTE_KEY);
    return !!home && Number(home) !== session.instituteId;
  } catch {
    return true;
  }
};

/**
 * Where an organization session should actually land: inside an institute.
 *
 * The organization is managed from an institute's My Organization tab, not
 * from the platform shell. So when the session that was just chosen is the
 * organization console (org:<oid>), this switches it — before anything is
 * rendered — to "View institute" on the signed-in owner's own institute (else
 * the group's first), with My Organization → Overview open, and returns that
 * institute's path. Returns null for any other session, or for a role that may
 * not enter an institute; the caller then lands as it always did.
 */
export async function organizationLandingPath(email?: string | null): Promise<string | null> {
  const session = getOrganizationSession();
  if (!session || session.instituteId !== null) return null;
  try {
    const { getConsole } = await import(
      "@/pages/superadmin-new/components/OrganizationManagement/Console/consoleApi"
    );
    const summary = await getConsole();
    if (!summary?.me?.can_enter || !summary.institutes?.length) return null;
    const mine = (email || "").trim().toLowerCase();
    const target =
      summary.institutes.find((i) =>
        [i.owner_email, ...(i.owners ?? [])].some((e) => (e || "").toLowerCase() === mine),
      ) ?? summary.institutes[0];
    await switchContext(`${ORG_PREFIX}${session.organizationId}:i:${target.institute_id}`);
    try {
      localStorage.setItem(HOME_INSTITUTE_KEY, String(target.institute_id));
      localStorage.setItem(`institute_${target.institute_id}_activeTab`, "myOrganization");
      localStorage.setItem(`institute_${target.institute_id}_activeSubTab`, "overview");
    } catch {
      // Storage blocked: the institute still opens, on its default tab.
    }
    return buildInstitutePath(target.institute_id, target.name);
  } catch {
    return null;
  }
}

/**
 * A Super Admin's "View institute" from a group's console. They can open any
 * institute already, so there is no sign-in context to switch - only the tab
 * to land on and the page to load.
 */
export function openInstituteDirect(
  instituteId: number,
  instituteName?: string | null,
  open?: InstituteTab,
): void {
  if (open?.tab) {
    try {
      localStorage.setItem(`institute_${instituteId}_activeTab`, open.tab);
      localStorage.setItem(`institute_${instituteId}_activeSubTab`, open.sub ?? "");
    } catch {
      // Storage blocked: the institute still opens, on its default tab.
    }
  }
  window.location.assign(buildInstitutePath(instituteId, instituteName));
}

/**
 * Back from an institute opened with "View institute" to the organization -
 * which lives in the owner's own institute's My Organization tab. Goes there
 * directly (organizationLandingPath); the standalone page is only the fallback
 * for a role that may not enter an institute.
 */
export async function backToOrganization(organizationId: number, email?: string | null): Promise<void> {
  await switchContext(`${ORG_PREFIX}${organizationId}`);
  const path = await organizationLandingPath(email);
  window.location.assign(path ?? "/admin_dashboard_page/my-organization");
}
